import { useId, type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { Text } from 'Text';
import accordionArrowDown from 'vectors/accordion_arrow_down.svg';
import '../locales/streamDashboard/streamDashboard-en.json';

/*
    The accordion box around one chapter's lessons. The children carry the padding, so the headers and the rule
    go full width.
*/

type Props = {
    chapterNumber: number;
    title: string;
    completedLessonCount: number;
    totalLessonCount: number;
    isExpanded: boolean;
    isPending: boolean;
    onToggle: () => void;
    children: ReactNode;
    className?: string;
};

export const ChapterBox: FC<Props> = ({
    chapterNumber,
    title,
    completedLessonCount,
    totalLessonCount,
    isExpanded,
    isPending,
    onToggle,
    children,
    className,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });
    const counts = { count: completedLessonCount, total: totalLessonCount };

    // Both the top bar and the bottom bar toggle the same panel, so they announce the same target.
    const panelId = useId();

    return (
        <div
            className={twMerge(
                'overflow-hidden rounded-8 border border-slate-grey-200 bg-white shadow-[0_2px_10px_0_rgba(0,0,0,0.1)]',
                className,
            )}
        >
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={isExpanded}
                aria-controls={isExpanded ? panelId : undefined}
                className="flex w-full items-center justify-between bg-slate-grey-50 px-3.5 py-2.5 leading-[35px] text-slate-grey sm:leading-[45px]"
            >
                <span className="text-xl font-semibold">
                    {t('chapter_number_x', { number: String(chapterNumber).padStart(2, '0') })}
                </span>
                <span className="text-xs font-medium uppercase max-sm:hidden sm:text-md">
                    {t('num_lessons_completed', counts)}
                </span>
                <span className="text-xs font-medium uppercase sm:hidden">
                    {t('num_lessons_completed_short', counts)}
                </span>
            </button>

            <div className="px-3.5">
                <Text
                    as="p"
                    weight="semibold"
                    className="my-3.5 text-xl leading-6 text-slate-grey-500 sm:leading-[27px]"
                >
                    {title.toUpperCase()}
                </Text>
            </div>
            <hr className="m-0 border-t border-slate-grey-200" />

            {/* No horizontal padding, so the divider runs full width. Each LessonRow insets its own text. */}
            {isExpanded && (
                <div id={panelId}>
                    {isPending && (
                        <Text as="p" className="px-3.5 py-3.5 text-md text-slate-grey">
                            {t('coming_soon')}
                        </Text>
                    )}
                    {children}
                </div>
            )}

            {/* The count sits at the end of the bar, which is what text-align: right gave the legacy
                bottom header, with the arrow pinned at the start. */}
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={isExpanded}
                aria-controls={isExpanded ? panelId : undefined}
                className="flex w-full items-center justify-between gap-2.5 px-3.5 py-2.5 text-md text-slate-grey"
            >
                <img
                    src={accordionArrowDown}
                    alt=""
                    className={`max-sm:hidden ${isExpanded ? 'rotate-180' : ''}`}
                    width="16"
                    height="16"
                />
                <span className="uppercase">{t('num_lessons_completed', counts)}</span>
            </button>
        </div>
    );
};
